import { useEffect, useMemo } from 'react'
import { useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import { clone as cloneSkeleton } from 'three/examples/jsm/utils/SkeletonUtils.js'
import { runtimePublicPath } from './runtimePublicPath'
import { resolveRigContactAnchors } from './rigRuntime'

export type FightPhysicalWeaponFamily='blade'|'axe'|'staff'|'shield'

export type FightPhysicalWeaponBinding={
  assetId:string
  family:FightPhysicalWeaponFamily
  tier:1|2|3
  hand:'right'|'left'
}

/**
 * Canonical Battle Arena -> Fight equipment continuity.
 *
 * Only heroes with an explicit weapon affinity in Battle Arena's
 * weaponsForHero() map are bound here. Unarmed/ambiguous identities stay
 * untouched instead of receiving a random weapon just to fill a slot.
 */
export const FIGHT_PHYSICAL_WEAPON_BINDINGS:Readonly<Record<string,FightPhysicalWeaponBinding>>=Object.freeze({
  'iron-bulwark':{assetId:'weapon-shield-mk3',family:'shield',tier:3,hand:'left'},
  'guardian-knight':{assetId:'weapon-blade-mk2',family:'blade',tier:2,hand:'right'},
  'sentinel':{assetId:'weapon-blade-mk2',family:'blade',tier:2,hand:'right'},
  'revenant':{assetId:'weapon-blade-mk2',family:'blade',tier:2,hand:'right'},
  'arcane-mage':{assetId:'weapon-staff-mk2',family:'staff',tier:2,hand:'right'},
  'void-seraph':{assetId:'weapon-staff-mk3',family:'staff',tier:3,hand:'right'},
  'quantum-archmage':{assetId:'weapon-staff-mk3',family:'staff',tier:3,hand:'right'},
  'shadow-assassin':{assetId:'weapon-blade-mk1',family:'blade',tier:1,hand:'right'},
  'cyber-vanguard':{assetId:'weapon-blade-mk2',family:'blade',tier:2,hand:'right'},
  'celestial-angel':{assetId:'weapon-blade-mk2',family:'blade',tier:2,hand:'right'},
  'dragon-summoner':{assetId:'weapon-blade-mk2',family:'blade',tier:2,hand:'right'},
  'alien-commander':{assetId:'weapon-axe-mk2',family:'axe',tier:2,hand:'right'},
  'wisp-warden':{assetId:'weapon-staff-mk2',family:'staff',tier:2,hand:'right'},
})

export function physicalWeaponBindingForFighter(fighterId:string):FightPhysicalWeaponBinding|null{
  return FIGHT_PHYSICAL_WEAPON_BINDINGS[fighterId]??null
}

export function fightPhysicalWeaponUrl(assetId:string){
  return runtimePublicPath(`/assets/vxmax/weapons/${assetId}.glb`)
}

const TRANSFORM:Record<FightPhysicalWeaponFamily,{
  position:[number,number,number]
  rotation:[number,number,number]
  scale:number
}>={
  blade:{position:[0,-.02,.04],rotation:[Math.PI*.5,0,Math.PI*.5],scale:.23},
  axe:{position:[0,-.03,.05],rotation:[Math.PI*.5,0,Math.PI*.5],scale:.20},
  staff:{position:[0,-.04,.08],rotation:[Math.PI*.5,0,0],scale:.17},
  shield:{position:[.02,0,.02],rotation:[0,Math.PI*.5,Math.PI*.5],scale:.24},
}

function prepareWeapon(scene:THREE.Object3D,assetId:string){
  const object=cloneSkeleton(scene)
  object.userData.assetId=assetId
  object.userData.inyronPhysicalWeapon=true
  object.traverse(node=>{
    if(!(node instanceof THREE.Mesh))return
    node.castShadow=true
    node.receiveShadow=true
    if(node.material instanceof THREE.MeshStandardMaterial){
      const material=node.material.clone()
      if(material.map)material.map.anisotropy=Math.max(material.map.anisotropy,8)
      if(material.normalMap)material.normalMap.anisotropy=Math.max(material.normalMap.anisotropy,8)
      if(material.roughnessMap)material.roughnessMap.anisotropy=Math.max(material.roughnessMap.anisotropy,8)
      if(material.metalnessMap)material.metalnessMap.anisotropy=Math.max(material.metalnessMap.anisotropy,8)
      node.material=material
    }
  })
  return object
}

/**
 * Mounts a certified physical VXMAX GLB on the fighter's authored hand bone.
 * Defensive shields use the left hand; offensive weapons use the right. The rig is the transform authority; no frame loop or procedural mesh
 * replaces the physical weapon.
 */
function LoadedFightPhysicalWeaponAttachment({binding,fighterRoot}:{binding:FightPhysicalWeaponBinding;fighterRoot:THREE.Object3D}){
  const url=fightPhysicalWeaponUrl(binding.assetId)
  const gltf=useGLTF(url)
  const weapon=useMemo(()=>prepareWeapon(gltf.scene,binding.assetId),[binding.assetId,gltf.scene])

  useEffect(()=>{
    const anchors=resolveRigContactAnchors(fighterRoot)
    const handName=binding.hand==='left'?anchors.hand_l:anchors.hand_r
    const hand=handName?fighterRoot.getObjectByName(handName):null
    if(!hand){
      fighterRoot.userData.__physicalWeapon={assetId:binding.assetId,status:'blocked',reason:`missing-${binding.hand}-hand-anchor`}
      return
    }
    const transform=TRANSFORM[binding.family]
    weapon.position.set(...transform.position)
    weapon.rotation.set(...transform.rotation)
    weapon.scale.setScalar(transform.scale)
    hand.add(weapon)
    fighterRoot.userData.__physicalWeapon={assetId:binding.assetId,family:binding.family,tier:binding.tier,status:'mounted',hand:handName,url}
    return()=>{
      hand.remove(weapon)
      delete fighterRoot.userData.__physicalWeapon
    }
  },[binding,fighterRoot,url,weapon])

  return null
}

export function FightPhysicalWeaponAttachment({fighterId,fighterRoot}:{fighterId:string;fighterRoot:THREE.Object3D}){
  const binding=physicalWeaponBindingForFighter(fighterId)
  return binding?<LoadedFightPhysicalWeaponAttachment binding={binding} fighterRoot={fighterRoot}/>:null
}
